/**
 * Functions for getting and setting URL values.
 *
 * @module url
 * @author EN GARDE (Martin Holler, Marcel Seelig)
 * @version 1.4
 *
 * Changelog:
 * v1.4 rewrite to module pattern
 * v1.3 setParameters added as separate function
 */

import _ from 'lodash';
import * as jsHelper from './js.js';

/**
 * Converts a query string (i.e. foo=bar&foo[]=bar&foo[]=baz) to an object.
 * This works just like PHPs $_GET.
 * @param {string} queryString
 * @return {object}
 */
export function decodeParameters(queryString) {
	return jsHelper.deserializeQueryString(queryString);
}

/**
 * Encodes an object as a query string (i.e. foo=bar&foo[]=bar&foo[]=baz).
 * @param {object} object
 * @param {boolean} [encodeUri=false] – Whether or not to escape string.
 * @return {string}
 */
export function encodeParameters(object, encodeUri) {
	object = object || {};
	encodeUri = encodeUri || false;

	var parameters = jsHelper.serializeQueryString(object);

	if (encodeUri) {
		parameters = encodeURIComponent(parameters);
	}

	return parameters;
}

/**
 * Returns an object of query parameters currently set in the URL.
 * @return {object}
 */
export function getQueryParameters() {
	return decodeParameters(location.search.substring(1));
}

/**
 * Updates a query object with given values.
 * @param {object} originalParameters - Original object to change.
 * @param {object} parameters - Object with values to merge with original.
 * @return {object}
 */
export function setParameters(originalParameters, parameters) {
	return _.mergeWith(originalParameters, parameters, function (objValue, srcValue) {
		if (_.isArray(objValue)) {
			return objValue.concat(srcValue);
		}
	});
}

/**
 * Sets query parameters from an object using the History API.
 * @param {object} object
 * @param {boolean} [mergeWithCurrent=true] - Whether or not to merge the
 * object with current query parameters.
 * @param {boolean} [encodeUri=false] – Whether or not to escape the URL before
 * setting it.
 * @param {string} [method=pushState] – History method – "replaceState" or
 * "pushState".
 * @return {string}
 */
export function setQueryParameters(parameters, mergeWithCurrent, encodeUri, method) {
	mergeWithCurrent = mergeWithCurrent === undefined ? true : !!mergeWithCurrent;
	encodeUri = encodeUri || false;

	if (!(history && history.pushState)) {
		return;
	}

	if (mergeWithCurrent) {
		setParameters(getQueryParameters(), parameters);
	}

	var fn = method === 'replaceState' ? history.replaceState : history.pushState;
	var params = encodeParameters(parameters, encodeUri);

	fn.apply(history, [undefined, undefined, '?' + params]);

	return '?' + params;
}

/**
 * Sets window.location.hash without causing a page jump. Warning: This does
 * not trigger the "hashchange" event!
 * @param {string} hash
 * @param {string} mode=pushState – "pushState" or "replaceState"
 * @return {string}
 */
export function setHash(hash, mode) {
	if (typeof hash !== 'string' || location.hash === '#' + hash || (hash === '' && !location.hash)) {
		return hash;
	}

	if (history.pushState) {
		if (mode === 'replaceState') {
			history.replaceState(null, null, '#' + hash);
		}
		else {
			history.pushState(null, null, '#' + hash);
		}
	}
	else {
		location.hash = hash;
	}

	return hash;
}

/**
 * Gets all parts (protocol, host, hostname, port, pathname, hash, search &
 * origin) of a given url.
 * @param {string} url
 * @return {object}
 */
export function getUrlParts(url) {
	if (typeof url !== 'string') {
		return {};
	}

	var a = document.createElement('a');
	a.href = url;

	return {
		protocol: a.protocol,	// http:
		host: a.host,			// example.com:3000
		hostname: a.hostname,	// example.com
		port: a.port,			// 3000
		pathname: a.pathname,	// /pathname/
		hash: a.hash,			// #hash
		search: a.search,		// ?search=test
		origin: a.origin		// http://example.com:3000
	};
}
